import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, Box, X, Terminal, Info, FileCode, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig';

const EVENTS = ['before_save', 'after_save', 'before_delete', 'after_delete', 'on_change'];

// 🎯 نفس بنية ActionScriptStudio.js القديمة (Monaco + autocomplete) بس
// موجّهة لمحرك تنفيذ جديد ومنفصل بالكامل (custom_script_engine.py) - الكونتكست
// هنا "record" (dict بيانات السجل) مش "source" (كائن SQLAlchemy كامل)، وقايمة
// الحقول للـ autocomplete جايه من حقول الموديل الكستم نفسه بس.
const CustomActionScriptStudio = () => {
  const { t, i18n } = useTranslation();
  const { slug, scriptId } = useParams();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const isNew = !scriptId || scriptId === 'new';

  const [fieldNames, setFieldNames] = useState([]);
  const [scriptData, setScriptData] = useState({
    script_name: '',
    event: 'before_save',
    field_name: '',
    script_content: '# Write your logic here\n# Use "record" (a dict) to read/modify the saved data\n# Example: if record.get("status") == "urgent":\n#     record["priority"] = "high"\n#\n# "action" is also available:\n#   action.log("debug message")            -> server console + browser console\n#   action.toast("Saved!", "success")      -> toast on screen (info/success/warning/error)\n#   action.prompt("Please review this")    -> blocking popup the user must close\n',
    is_active: true,
  });
  const [consoleLogs, setConsoleLogs] = useState([
    { time: new Date().toLocaleTimeString(), msg: t('studio.console.init', 'Studio ready'), type: 'info' },
  ]);

  const addLog = (msg, type = 'info') => {
    setConsoleLogs((prev) => [...prev, { time: new Date().toLocaleTimeString(), msg, type }]);
  };

  useEffect(() => {
    api.get(`/custom-models/${slug}`)
      .then((res) => setFieldNames((res.data.fields || []).map((f) => f.field_name)))
      .catch(() => addLog(t('studio.console.schema_fail', 'Failed to load field schema'), 'error'));

    if (!isNew) {
      api.get(`/custom-models/${slug}/scripts`)
        .then((res) => {
          const found = (res.data || []).find((s) => String(s.id) === String(scriptId));
          if (found) setScriptData(found);
        })
        .catch(() => toast.error(t('scripts.toasts.fetch_fail', 'Failed to load script')));
    }
  }, [slug, scriptId, isNew]);

  const handleEditorWillMount = (monaco) => {
    monacoRef.current = monaco;
    monaco.languages.registerCompletionItemProvider('python', {
      triggerCharacters: ['.', '['],
      provideCompletionItems: (model, position) => {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber, startColumn: 1,
          endLineNumber: position.lineNumber, endColumn: position.column,
        });
        if (textUntilPosition.endsWith('record[') || textUntilPosition.endsWith("record['") || textUntilPosition.endsWith('record.get(')) {
          return {
            suggestions: fieldNames.map((f) => ({
              label: f,
              kind: monaco.languages.CompletionItemKind.Field,
              insertText: `"${f}"`,
              detail: t('studio.field_from', 'Field from') + ' ' + slug,
            })),
          };
        }
        if (textUntilPosition.endsWith('action.')) {
          return {
            suggestions: [
              { label: 'log', kind: monaco.languages.CompletionItemKind.Method, insertText: 'log("$1")', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Console log (server + browser)' },
              { label: 'toast', kind: monaco.languages.CompletionItemKind.Method, insertText: 'toast("$1", "info")', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Show a toast on screen (info/success/warning/error)' },
              { label: 'prompt', kind: monaco.languages.CompletionItemKind.Method, insertText: 'prompt("$1")', insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet, detail: 'Show a blocking popup the user must close' },
            ],
          };
        }
        return { suggestions: [] };
      },
    });
  };

  const handleSave = async () => {
    if (!scriptData.script_name) {
      toast.error(t('customApps.scripts.nameRequired', 'Script name is required'));
      return;
    }
    if (scriptData.event === 'on_change' && !scriptData.field_name) {
      toast.error(t('customApps.scripts.fieldRequired', 'Pick which field triggers this script'));
      return;
    }
    try {
      if (isNew) {
        await api.post(`/custom-models/${slug}/scripts`, scriptData);
      } else {
        await api.put(`/custom-action-scripts/${scriptId}`, scriptData);
      }
      toast.success(t('studio.toasts.preserved', 'Saved'));
      addLog(t('studio.console.saved_prefix', 'Saved:') + ` "${scriptData.script_name}"`, 'success');
      navigate(`/admin/custom-apps/${slug}/scripts`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('workflow.toasts.save_fail', 'Save failed'));
      addLog(t('studio.console.error_op', 'Error'), 'error');
    }
  };

  const insertAtCursor = (text) => {
    const editor = editorRef.current;
    if (editor) {
      const selection = editor.getSelection();
      editor.executeEdits('my-source', [{ range: selection, text, forceMoveMarkers: true }]);
      editor.focus();
    }
  };

  return (
    <div className={`flex h-screen bg-[#0b0e14] text-slate-300 overflow-hidden select-none ${isRTL ? 'font-cairo' : 'font-sans'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`w-72 bg-[#141923] flex flex-col shadow-2xl ${isRTL ? 'border-l order-2' : 'border-r order-1'} border-slate-800`}>
        <div className="p-4 bg-[#1c2331] border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-[10px] font-black text-orange-500 uppercase tracking-widest flex items-center gap-2">
            <Box size={14} /> {slug}
          </h2>
        </div>
        <div className="p-4 border-b border-slate-800 bg-[#0f131a]">
          <label className="text-[9px] font-black text-slate-500 uppercase mb-2 block tracking-widest">{t('customApps.scripts.event', 'Event')}</label>
          <select
            className="w-full bg-[#0b0e14] border border-slate-700 rounded p-2 text-xs font-bold outline-none text-blue-400 focus:border-orange-500"
            value={scriptData.event}
            onChange={(e) => setScriptData({ ...scriptData, event: e.target.value })}
          >
            {EVENTS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
          </select>
          {scriptData.event === 'on_change' && (
            <>
              <label className="text-[9px] font-black text-slate-500 uppercase mt-3 mb-2 block tracking-widest">{t('customApps.scripts.triggerField', 'Trigger field')}</label>
              <select
                className="w-full bg-[#0b0e14] border border-slate-700 rounded p-2 text-xs font-bold outline-none text-blue-400 focus:border-orange-500"
                value={scriptData.field_name || ''}
                onChange={(e) => setScriptData({ ...scriptData, field_name: e.target.value })}
              >
                <option value="">— {t('customApps.studio.selectAField', 'Select a field')} —</option>
                {fieldNames.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </>
          )}
        </div>
        <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
          <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest px-2 py-2">
            {t('customApps.scripts.actions', 'action')}
          </div>
          {[
            { snippet: 'action.log("")', label: 'action.log(...)' },
            { snippet: 'action.toast("", "info")', label: 'action.toast(...)' },
            { snippet: 'action.prompt("")', label: 'action.prompt(...)' },
          ].map((a) => (
            <button
              key={a.label}
              onClick={() => insertAtCursor(a.snippet)}
              className="w-full text-left px-3 py-1.5 text-[10px] text-blue-400 hover:text-white hover:bg-orange-500/10 rounded transition-all flex items-center gap-2 font-mono"
            >
              <div className="w-1 h-1 bg-blue-500 rounded-full" /> {a.label}
            </button>
          ))}
          <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest px-2 py-2 mt-2">
            {t('customApps.scripts.fields', 'record fields')}
          </div>
          {fieldNames.map((f) => (
            <button
              key={f}
              onClick={() => insertAtCursor(`record["${f}"]`)}
              className="w-full text-left px-3 py-1.5 text-[10px] text-slate-500 hover:text-white hover:bg-orange-500/10 rounded transition-all flex items-center gap-2"
            >
              <div className="w-1 h-1 bg-slate-700 rounded-full" /> {f}
            </button>
          ))}
        </div>
      </div>

      <div className={`flex-1 flex flex-col relative ${isRTL ? 'order-1' : 'order-2'}`}>
        <div className="h-16 bg-[#141923] border-b border-slate-800 flex items-center justify-between px-6 z-10 shadow-lg">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/admin/custom-apps/${slug}/scripts`)} className="text-slate-500 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div className="bg-orange-500/10 p-2 rounded"><FileCode className="text-orange-500" size={20} /></div>
            <input
              className="bg-transparent border-b border-slate-700 focus:border-orange-500 outline-none px-2 py-1 text-sm font-bold text-white w-64 transition-all placeholder:text-slate-700"
              placeholder={t('studio.name_placeholder', 'Script name')}
              value={scriptData.script_name}
              onChange={(e) => setScriptData({ ...scriptData, script_name: e.target.value })}
            />
            <div className="flex items-center gap-2 text-[10px] font-black text-slate-500 bg-black/30 px-3 py-1.5 rounded-full border border-white/5">
              <Info size={12} className="text-blue-400" /> {t('customApps.scripts.engineInfo', 'Sandboxed engine — restricted builtins')}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleSave} className="flex items-center gap-2 px-5 py-2 rounded text-[11px] font-black bg-orange-600 hover:bg-orange-500 text-white transition-all shadow-lg active:scale-95">
              <Save size={14} /> {t('studio.btns.save_deploy', 'Save')}
            </button>
            <div className="w-[1px] h-6 bg-slate-800 mx-2" />
            <button onClick={() => navigate(`/admin/custom-apps/${slug}/scripts`)} className="p-2 hover:bg-red-500/10 hover:text-red-500 rounded transition-all"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 bg-[#0b0e14] relative" dir="ltr">
          <Editor
            height="100%"
            theme="vs-dark"
            defaultLanguage="python"
            value={scriptData.script_content}
            beforeMount={handleEditorWillMount}
            onMount={(editor) => { editorRef.current = editor; }}
            onChange={(v) => setScriptData({ ...scriptData, script_content: v })}
            options={{
              fontSize: 15,
              fontFamily: 'Fira Code, SFMono-Regular, Consolas, monospace',
              minimap: { enabled: true },
              automaticLayout: true,
              scrollBeyondLastLine: false,
              lineNumbers: 'on',
              padding: { top: 20 },
              bracketPairColorization: { enabled: true },
            }}
          />
        </div>

        <div className="h-40 bg-[#0b0e14] border-t border-slate-800 flex flex-col shadow-inner">
          <div className="p-2 bg-[#141923] border-b border-slate-800 flex justify-between items-center px-4">
            <div className="flex items-center gap-2">
              <Terminal size={12} className="text-orange-500" />
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{t('studio.console_title', 'Console')}</span>
            </div>
            <button onClick={() => setConsoleLogs([])} className="text-[9px] font-black text-slate-600 hover:text-slate-400 uppercase">{t('studio.clear_console', 'Clear')}</button>
          </div>
          <div className="p-4 font-mono text-[11px] overflow-y-auto custom-scrollbar flex-1 bg-black/20">
            {consoleLogs.map((log, i) => (
              <div key={i} className="mb-1 flex gap-3">
                <span className="text-slate-600">[{log.time}]</span>
                <span className={log.type === 'error' ? 'text-red-500' : log.type === 'success' ? 'text-green-500' : 'text-blue-400'}>
                  {log.type === 'error' ? '✖' : log.type === 'success' ? '✔' : 'ℹ'} {log.msg}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomActionScriptStudio;
